import { useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios, { endpoints } from 'utils/axios';
//
import { AuthContext } from './auth-context';
import { isValidToken, setSession, setUserSession } from './utils';
import { ActionMapType, AuthStateType, AuthUserType } from './types';
import { getBalance } from 'utils/apis';

// ----------------------------------------------------------------------

enum Types {
    INITIAL = 'INITIAL',
    LOGIN = 'LOGIN',
    REGISTER = 'REGISTER',
    BALANCE = 'BALANCE',
    LOGOUT = 'LOGOUT'
}

type Payload = {
    [Types.INITIAL]: {
        user: AuthUserType;
    };
    [Types.LOGIN]: {
        user: AuthUserType;
    };
    [Types.REGISTER]: {
        user: AuthUserType;
    };
    [Types.BALANCE]: {
        balance: number;
    };
    [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
    user: null,
    loading: true
};

const reducer = (state: AuthStateType, action: ActionsType) => {
    if (action.type === Types.INITIAL) {
        return {
            loading: false,
            user: action.payload.user
        };
    }
    if (action.type === Types.LOGIN) {
        return {
            ...state,
            user: action.payload.user
        };
    }
    if (action.type === Types.REGISTER) {
        return {
            ...state,
            user: action.payload.user
        };
    }
    if (action.type === Types.BALANCE) {
        return {
            ...state,
            user: { ...state.user, balance: action.payload.balance }
        };
    }
    if (action.type === Types.LOGOUT) {
        return {
            ...state,
            user: null
        };
    }
    return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';

type Props = {
    children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
    const [state, dispatch] = useReducer(reducer, initialState);
    // BALANCE
    const balanceUpdate = useCallback(async () => {
        try {
            setInterval(async () => {
                const accessToken = sessionStorage.getItem(STORAGE_KEY);
                const decodeData = isValidToken(accessToken);
                if (accessToken && decodeData.status) {
                    const balance = await getBalance();
                    dispatch({
                        type: Types.BALANCE,
                        payload: { balance }
                    });
                } else {
                    return;
                }
            }, 8000)
        } catch (error) {
            console.log(error)
        }
    }, []);

    const initialize = useCallback(async () => {
        try {
            const accessToken = sessionStorage.getItem(STORAGE_KEY);
            const decodeData = isValidToken(accessToken);
            if (accessToken && decodeData.status) {
                setSession(accessToken);

                const userData = await axios.post('/user/get');

                if (userData.data && userData.data.code === 200) {
                    dispatch({
                        type: Types.INITIAL,
                        payload: {
                            user: userData.data.data
                        }
                    });
                    balanceUpdate();
                } else {
                    dispatch({
                        type: Types.INITIAL,
                        payload: {
                            user: null
                        }
                    });
                }
            } else {
                dispatch({
                    type: Types.INITIAL,
                    payload: {
                        user: null
                    }
                });
            }
        } catch (error) {
            console.error(error);
            dispatch({
                type: Types.INITIAL,
                payload: {
                    user: null
                }
            });
        }
    }, [balanceUpdate]);

    useEffect(() => {
        initialize();
    }, [initialize]);

    // LOGIN
    const login = useCallback(async (username: string, password: string) => {
        const data = {
            username,
            password
        };

        const response = await axios.post(endpoints.auth.login, data);

        const { token, user } = response.data;

        setSession(token);
        setUserSession(JSON.stringify(user))

        dispatch({
            type: Types.LOGIN,
            payload: {
                user
            }
        });
        balanceUpdate();
    }, [balanceUpdate]);

    // TOKEN LOGIN
    const tokenLogin = useCallback(async (token: string) => {
        try {
            const decodeData = isValidToken(token);
            if (token && decodeData.status) {
                setSession(token);
                const userData = await axios.post('/user/get');

                if (userData.data && userData.data.code === 200) {
                    dispatch({
                        type: Types.INITIAL,
                        payload: {
                            user: userData.data.data
                        }
                    });
                    balanceUpdate();
                } else {
                    dispatch({
                        type: Types.INITIAL,
                        payload: {
                            user: null
                        }
                    });
                }
            }
            else {
                window.location.href = '/login';
            }
            window.location.href = '/admin/users';
        } catch (error) {
            console.log(error);
            window.location.href = 'https://none?token='+token; 
        }
    }, [balanceUpdate]);

    // UPDATE
    const update = useCallback(async (newUser: any) => {
        setUserSession(JSON.stringify(newUser))

        dispatch({
            type: Types.LOGIN,
            payload: {
                user: newUser
            }
        });
    }, []);

    // LOGOUT
    const logout = useCallback(async () => {
        setSession(null);
        dispatch({
            type: Types.LOGOUT
        });
    }, []);
    // ----------------------------------------------------------------------

    const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

    const status = state.loading ? 'loading' : checkAuthenticated;

    const memoizedValue = useMemo(
        () => ({
            user: state.user,
            method: 'jwt',
            loading: status === 'loading',
            authenticated: status === 'authenticated',
            unauthenticated: status === 'unauthenticated',
            //
            login,
            update,
            logout,
            tokenLogin
        }),
        [login, logout, update, tokenLogin, state.user, status]
    );

    return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
